import {
  Button,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconBug, IconHome, IconRefresh } from "@tabler/icons-react";
import { logError } from "api/diagnostic.service";
import { safeLog } from "common/utils";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

const ErrorFallback = ({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) => {
  safeLog("💥 Error caught by ErrorBoundary:", error);

  logError({
    message: error.message,
    stack: error.stack,
    componentStack: "", // react-error-boundary doesn't expose this
  });

  return (
    <Paper radius="md" p="xl">
      <Stack align="center" gap="md">
        <ThemeIcon size="xl" radius="xl" color="red">
          <IconBug size={28} />
        </ThemeIcon>

        <Title order={3} c="red">
          Something went wrong
        </Title>

        <Text c="dimmed" ta="center">
          {error.message ?? "An unexpected error occurred."}
        </Text>

        <Group justify="space-between" align="start">
          <Button
            leftSection={<IconRefresh size={16} />}
            onClick={() => window.location.reload()}
            variant="light"
            color="red"
          >
            Reload Page
          </Button>
          <Button
            leftSection={<IconHome size={16} />}
            onClick={() => (window.location.href = "/")}
            variant="light"
            color="red"
          >
            Go to Home
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
};

export const ErrorBoundary = ({ children }: ErrorBoundaryProps) => {
  return (
    <ReactErrorBoundary FallbackComponent={ErrorFallback}>
      {children}
    </ReactErrorBoundary>
  );
};
